import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { PayeePopupComponent } from '../../../shared/payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { MatError } from '@angular/material/input';

type NewPasswordControls = {
  newPwd: FormControl<string>;
  newPwdConfirm: FormControl<string>;
};

type PasswordFormControls = {
  oldPwd: FormControl<string>;
  newPassword: FormGroup<NewPasswordControls>;
};

function confirmPasswordMatch(
  group: AbstractControl,
): ValidationErrors | null {
  const { newPwd, newPwdConfirm } = (group as FormGroup<NewPasswordControls>)
    .controls;

  if (newPwd.value !== newPwdConfirm.value) {
    newPwd.setErrors({ ...newPwd.errors, confirmPasswordMatch: true });
  } else if (newPwd.errors) {
    const errors = { ...newPwd.errors };
    delete errors['confirmPasswordMatch'];
    newPwd.setErrors(Object.keys(errors).length ? errors : null);
  }

  return null;
}

function oldAndNewPasswordMatch(
  group: AbstractControl,
): ValidationErrors | null {
  const { oldPwd, newPassword } = (group as FormGroup<PasswordFormControls>)
    .controls;
  const newPwd = newPassword.controls.newPwd;

  if (oldPwd.value === newPwd.value) {
    newPwd.setErrors({ ...newPwd.errors, oldAndNewPasswordMatch: true });
  } else if (newPwd.errors) {
    const errors = { ...newPwd.errors };
    delete errors['oldAndNewPasswordMatch'];
    newPwd.setErrors(Object.keys(errors).length ? errors : null);
  }

  return null;
}

@Component({
  selector: 'app-change-password-dialog',
  templateUrl: './change-password-dialog.component.html',
  styleUrls: ['./change-password-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    PayeeIconComponent,
    PayeeButtonComponent,
    MatError,
    MatDialogClose,
    TranslateModule,
  ],
})
export class ChangePasswordDialogComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private dialogRef =
    inject<MatDialogRef<ChangePasswordDialogComponent>>(MatDialogRef);
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  readonly passwordForm = this.fb.group<PasswordFormControls>(
    {
      oldPwd: this.fb.control('', {
        validators: [Validators.required, Validators.minLength(6)],
        nonNullable: true,
      }),
      newPassword: this.fb.group<NewPasswordControls>(
        {
          newPwd: this.fb.control('', {
            validators: [Validators.required, Validators.minLength(6)],
            nonNullable: true,
          }),
          newPwdConfirm: this.fb.control('', {
            validators: [Validators.required, Validators.minLength(6)],
            nonNullable: true,
          }),
        },
        { validators: [confirmPasswordMatch] },
      ),
    },
    { validators: [oldAndNewPasswordMatch] },
  );

  get oldPwd() {
    return this.passwordForm.controls.oldPwd;
  }
  get newPassword() {
    return this.passwordForm.controls.newPassword;
  }
  get newPwd() {
    return this.newPassword.controls.newPwd;
  }
  get newPwdConfirm() {
    return this.newPassword.controls.newPwdConfirm;
  }

  async onClose(): Promise<void> {
    if (this.passwordForm.invalid) {
      return;
    }

    const {
      oldPwd,
      newPassword: { newPwd, newPwdConfirm },
    } = this.passwordForm.getRawValue();

    try {
      await this.authService.forceResetPassword({
        old_password: oldPwd,
        password: newPwd,
        password_confirmation: newPwdConfirm,
      });

      this.snackbar.open(
        this.translate.instant('PASSWORD_RESET_DIALOG.SUCCESS_MESSAGE'),
        this.translate.instant('OK'),
        {
          duration: 5000,
        },
      );
      this.dialogRef.close();
    } catch (err: unknown) {
      console.error(err);
      const code: string =
        (err as { response?: { data?: { code?: string } } })?.response?.data
          ?.code ?? '';
      if (code === 'invalid-password') {
        this.snackbar.open(
          this.translate.instant(
            'PASSWORD_RESET_DIALOG.ERROR_MESSAGES.OLD_PASSWORD_WRONG',
          ),
          this.translate.instant('OK'),
          {
            duration: 5000,
          },
        );
      } else {
        this.snackbar.open(
          this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
          this.translate.instant('OK'),
          {
            duration: 5000,
          },
        );
      }
    }
  }

}
